import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  Refresh as RefreshIcon,
  Money as MoneyIcon,
  PhoneIphone as PhoneIphoneIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Debt, DebtStatus, DebtSourceType } from '../utils/debtTypes';
import { 
  getAllDebts, 
  addDebtPayment, 
  getDebtsByCustomer,
  getDebtsByStatus,
  getDebtsBySourceType 
} from '../services/debtService';
import { formatCurrency } from '../utils/formatters';

// واجهة للإحصائيات
interface DebtStats {
  totalDebts: number;
  totalAmount: number;
  totalPaid: number;
  totalRemaining: number;
  activeDebts: number;
  salesDebts: number;
  maintenanceDebts: number;
}

// واجهة للفلاتر
interface DebtFilters {
  searchTerm: string;
  sourceType: DebtSourceType | 'ALL';
  status: DebtStatus | 'ALL';
  customerName: string;
}

const UnifiedDebtsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [debts, setDebts] = useState<Debt[]>([]);
  const [filteredDebts, setFilteredDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DebtStats>({
    totalDebts: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalRemaining: 0,
    activeDebts: 0,
    salesDebts: 0,
    maintenanceDebts: 0
  });

  // حالات الفلاتر
  const [filters, setFilters] = useState<DebtFilters>({
    searchTerm: '',
    sourceType: 'ALL',
    status: 'ALL',
    customerName: ''
  });

  // حالات نافذة الدفع
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [walletNumber, setWalletNumber] = useState<string>('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  // المحافظ الإلكترونية
  const electronicWalletMethods = ['vodafone_wallet', 'instapay', 'etisalat_wallet'];

  // تحميل البيانات
  useEffect(() => {
    loadDebts();
  }, []);

  // تطبيق الفلاتر
  useEffect(() => {
    applyFilters();
  }, [debts, filters]);

  const loadDebts = async () => {
    try {
      setLoading(true);
      const allDebts = await getAllDebts();
      setDebts(allDebts);
      calculateStats(allDebts);
    } catch (error) {
      console.error('Error loading debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (debtsList: Debt[]) => {
    const stats: DebtStats = {
      totalDebts: debtsList.length,
      totalAmount: debtsList.reduce((sum, debt) => sum + debt.amount, 0),
      totalPaid: debtsList.reduce((sum, debt) => sum + debt.paidAmount, 0),
      totalRemaining: debtsList.reduce((sum, debt) => sum + debt.remainingAmount, 0),
      activeDebts: debtsList.filter(debt => debt.status === DebtStatus.ACTIVE || debt.status === DebtStatus.PARTIALLY_PAID).length,
      salesDebts: debtsList.filter(debt => debt.sourceType === DebtSourceType.SALE).length,
      maintenanceDebts: debtsList.filter(debt => debt.sourceType === DebtSourceType.MAINTENANCE).length
    };
    setStats(stats);
  };

  const applyFilters = () => {
    let filtered = [...debts];

    // فلتر البحث
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(debt => 
        debt.customerName.toLowerCase().includes(searchLower) ||
        debt.sourceReference.toLowerCase().includes(searchLower) ||
        debt.description.toLowerCase().includes(searchLower)
      );
    }

    // فلتر نوع المصدر
    if (filters.sourceType !== 'ALL') {
      filtered = filtered.filter(debt => debt.sourceType === filters.sourceType);
    }

    // فلتر الحالة
    if (filters.status !== 'ALL') {
      filtered = filtered.filter(debt => debt.status === filters.status);
    }

    // فلتر اسم العميل
    if (filters.customerName) {
      const customerLower = filters.customerName.toLowerCase();
      filtered = filtered.filter(debt => 
        debt.customerName.toLowerCase().includes(customerLower)
      );
    }

    setFilteredDebts(filtered);
  };

  const handlePaymentClick = (debt: Debt) => {
    setSelectedDebt(debt);
    setPaymentAmount(debt.remainingAmount.toString());
    setPaymentMethod('cash');
    setWalletNumber('');
    setPaymentNotes('');
    setPaymentDialog(true);
  };

  const handlePayment = async () => {
    if (!selectedDebt || !currentUser) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0 || amount > selectedDebt.remainingAmount) {
      alert('مبلغ الدفع غير صحيح');
      return;
    }

    // التحقق من رقم المحفظة للمحافظ الإلكترونية
    if (electronicWalletMethods.includes(paymentMethod) && !walletNumber.trim()) {
      alert('يرجى إدخال رقم المحفظة/الحساب المحول عليه');
      return;
    }

    try {
      setProcessingPayment(true);

      // إضافة رقم المحفظة إلى الملاحظات إذا كانت محفظة إلكترونية
      let finalNotes = paymentNotes || `دفعة دين - ${new Date().toLocaleDateString('ar-EG')}`;
      if (electronicWalletMethods.includes(paymentMethod) && walletNumber.trim()) {
        const walletLabel =
          paymentMethod === 'vodafone_wallet' ? 'فودافون كاش' :
          paymentMethod === 'instapay' ? 'انستاباي' :
          'اتصالات كاش';
        finalNotes += ` - ${walletLabel} (${walletNumber})`;
      }

      await addDebtPayment(
        selectedDebt.id,
        amount,
        paymentMethod,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        finalNotes
      );

      // إعادة تحميل البيانات
      await loadDebts();

      setPaymentDialog(false);
      setSelectedDebt(null);
      alert('تم تسجيل الدفعة بنجاح');

    } catch (error) {
      console.error('Error processing payment:', error);
      alert('حدث خطأ أثناء معالجة الدفعة');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusColor = (status: DebtStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case DebtStatus.ACTIVE: return 'error';
      case DebtStatus.PARTIALLY_PAID: return 'warning';
      case DebtStatus.PAID: return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: DebtStatus): string => {
    switch (status) {
      case DebtStatus.ACTIVE: return 'نشط';
      case DebtStatus.PARTIALLY_PAID: return 'مدفوع جزئياً';
      case DebtStatus.PAID: return 'مدفوع';
      default: return status;
    }
  };

  const getSourceTypeLabel = (sourceType: DebtSourceType): string => {
    switch (sourceType) {
      case DebtSourceType.SALE: return 'مبيعات';
      case DebtSourceType.MAINTENANCE: return 'صيانة';
      default: return sourceType;
    }
  };

  const getSourceIcon = (sourceType: DebtSourceType) => {
    return sourceType === DebtSourceType.SALE ? <ReceiptIcon /> : <BuildIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان والأزرار */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إدارة الديون الموحدة
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDebts}
          disabled={loading}
        >
          تحديث
        </Button>
      </Box>

      {/* بطاقات الإحصائيات */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                إجمالي الديون
              </Typography>
              <Typography variant="h5">
                {stats.totalDebts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                إجمالي المبلغ
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                المبلغ المدفوع
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(stats.totalPaid)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                المبلغ المتبقي
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatCurrency(stats.totalRemaining)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* شريط الفلاتر */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="البحث"
                value={filters.searchTerm}
                onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="اسم العميل، رقم المرجع، الوصف..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>نوع المصدر</InputLabel>
                <Select
                  value={filters.sourceType}
                  label="نوع المصدر"
                  onChange={(e) => setFilters(prev => ({ ...prev, sourceType: e.target.value as DebtSourceType | 'ALL' }))}
                >
                  <MenuItem value="ALL">الكل</MenuItem>
                  <MenuItem value={DebtSourceType.SALE}>مبيعات</MenuItem>
                  <MenuItem value={DebtSourceType.MAINTENANCE}>صيانة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={filters.status}
                  label="الحالة"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as DebtStatus | 'ALL' }))}
                >
                  <MenuItem value="ALL">الكل</MenuItem>
                  <MenuItem value={DebtStatus.ACTIVE}>نشط</MenuItem>
                  <MenuItem value={DebtStatus.PARTIALLY_PAID}>مدفوع جزئياً</MenuItem>
                  <MenuItem value={DebtStatus.PAID}>مدفوع</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="اسم العميل"
                value={filters.customerName}
                onChange={(e) => setFilters(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="فلترة حسب اسم العميل"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilters({
                  searchTerm: '',
                  sourceType: 'ALL',
                  status: 'ALL',
                  customerName: ''
                })}
              >
                مسح الفلاتر
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* جدول الديون */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              قائمة الديون ({filteredDebts.length})
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>العميل</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>المرجع</TableCell>
                  <TableCell>المبلغ الأصلي</TableCell>
                  <TableCell>المدفوع</TableCell>
                  <TableCell>المتبقي</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>تاريخ الإنشاء</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDebts.map((debt) => (
                  <TableRow key={debt.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {debt.customerName}
                        </Typography>
                        {debt.customerPhone && (
                          <Typography variant="caption" color="textSecondary">
                            {debt.customerPhone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getSourceIcon(debt.sourceType)}
                        label={getSourceTypeLabel(debt.sourceType)}
                        size="small"
                        variant="outlined"
                        color={debt.sourceType === DebtSourceType.SALE ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {debt.sourceReference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(debt.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main">
                        {formatCurrency(debt.paidAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={debt.remainingAmount > 0 ? "error.main" : "success.main"}
                      >
                        {formatCurrency(debt.remainingAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(debt.status)}
                        size="small"
                        color={getStatusColor(debt.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {debt.createdAt instanceof Date
                          ? debt.createdAt.toLocaleDateString('ar-EG')
                          : new Date(debt.createdAt).toLocaleDateString('ar-EG')
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {debt.remainingAmount > 0 && (
                          <Tooltip title="دفع">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePaymentClick(debt)}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="عرض التفاصيل">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              // TODO: إضافة نافذة تفاصيل الدين
                              console.log('View debt details:', debt);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredDebts.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                لا توجد ديون تطابق معايير البحث
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* نافذة الدفع */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          دفع دين - {selectedDebt?.customerName}
        </DialogTitle>
        <DialogContent>
          {selectedDebt && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>المبلغ المتبقي:</strong> {formatCurrency(selectedDebt.remainingAmount)}
                </Typography>
                <Typography variant="body2">
                  <strong>المصدر:</strong> {getSourceTypeLabel(selectedDebt.sourceType)} - {selectedDebt.sourceReference}
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="مبلغ الدفع"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    inputProps={{
                      min: 0,
                      max: selectedDebt.remainingAmount,
                      step: 0.01
                    }}
                    helperText={`الحد الأقصى: ${formatCurrency(selectedDebt.remainingAmount)}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>طريقة الدفع</InputLabel>
                    <Select
                      value={paymentMethod}
                      label="طريقة الدفع"
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setWalletNumber(''); // مسح رقم المحفظة عند تغيير طريقة الدفع
                      }}
                    >
                      <MenuItem value="cash">
                        <Box display="flex" alignItems="center" gap={1}>
                          <MoneyIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">نقدي</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Cash
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="vodafone_wallet">
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIphoneIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">محفظة فودافون</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Vodafone Cash
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="instapay">
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIphoneIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">انستاباي</Typography>
                            <Typography variant="caption" color="text.secondary">
                              InstaPay
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="etisalat_wallet">
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIphoneIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">اتصالات كاش</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Etisalat Cash
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="debt_reduction">
                        <Box display="flex" alignItems="center" gap={1}>
                          <RemoveCircleOutlineIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">تخفيض دين</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Debt Reduction
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* رقم المحفظة - يظهر فقط للمحافظ الإلكترونية */}
                {electronicWalletMethods.includes(paymentMethod) && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="رقم المحفظة/الحساب المحول عليه"
                      value={walletNumber}
                      onChange={(e) => setWalletNumber(e.target.value)}
                      placeholder={
                        paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
                        paymentMethod === 'instapay' ? '1234567890123456' :
                        '01xxxxxxxxx'
                      }
                      required
                      helperText={`مطلوب لتأكيد ${
                        paymentMethod === 'vodafone_wallet' ? 'تحويل فودافون كاش' :
                        paymentMethod === 'instapay' ? 'التحويل البنكي' :
                        'تحويل اتصالات كاش'
                      }`}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ملاحظات"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="ملاحظات إضافية (اختياري)"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handlePayment}
            disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}
            startIcon={processingPayment ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {processingPayment ? 'جاري المعالجة...' : 'تأكيد الدفع'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedDebtsPage;
